import { DatePicker } from '@components/ui/date-picker';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { MultiSelect } from '@components/ui/multi-select';
import { SearchableSelect } from '@components/ui/searchable-select';
import { UNKNOWN_DATE_VALUE } from '@utils/date';
import { format } from 'date-fns';
import React, { useMemo } from 'react';
import ReactPhoneInput from 'react-phone-input-2';

import { nonNegativeInputProps } from '@/components/numericInput';
import type { GetFieldSuggestionProps } from '@/hooks/useCaseInputStorage';
import type { FormField } from '@/types/formSchema';
import { computeAutoCalculatedValue } from '@/utils/autoCalculate';

import { DobField } from './DobField';
import { FileField } from './FileField';
import { FullNameField } from './FullNameField';
import { InputWithSuggestion } from './InputWithSuggestion';

interface FormFieldRendererProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  onBlur?: () => void;
  error?: string;
  /** Consolidated per-field suggestion props from useCaseInputStorage */
  getFieldSuggestionProps?: GetFieldSuggestionProps;
  /**
   * Case ID for File-type fields on an existing case. When set, file selections upload immediately;
   * when absent (Manual Case Creation) the File is deferred to submit. Ignored by other field types.
   */
  caseRecordId?: string;
  /**
   * When true, suppress the built-in label/header block — render only the input (plus the
   * suggestion path, description, and error). Used by callers that already render their own
   * inline label (e.g. the Human Call script step and field board) to avoid a duplicate label.
   */
  hideLabel?: boolean;
  /**
   * Render controls at a denser height (h-8 vs h-11) for compact inline-edit
   * contexts like the Human Call field board. Default false → callers unchanged.
   */
  compact?: boolean;
  /**
   * Explicit control-height class, overriding the `compact` default. Use when a host
   * section has its own established control height to match — e.g. the case Output
   * board renders its inputs at `h-8`.
   */
  controlHeightClass?: string;
}

const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  getFieldSuggestionProps,
  caseRecordId,
  hideLabel = false,
  compact = false,
  controlHeightClass,
}) => {
  // Get suggestion props for this field (if available)
  const sp = getFieldSuggestionProps?.(field.id, field.type);
  const hasSuggestionOrHint =
    sp?.suggestion !== undefined || sp?.hint !== undefined || !!sp?.options?.length;

  const controlH = controlHeightClass ?? (compact ? 'h-4' : 'h-11');

  const renderField = () => {
    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <Input
            id={field.id}
            type={field.type}
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={field.placeholder}
            disabled={field.disabled}
            className={`${controlH} ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
          />
        );

      case 'number':
        return (
          <Input
            id={field.id}
            type="number"
            value={value ?? ''}
            onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
            onBlur={onBlur}
            placeholder={field.placeholder}
            disabled={field.disabled}
            {...nonNegativeInputProps}
            className={`${controlH} ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
          />
        );

      case 'float':
        return (
          <Input
            id={field.id}
            type="number"
            step="any"
            value={value ?? ''}
            onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
            onBlur={onBlur}
            placeholder={field.placeholder}
            disabled={field.disabled}
            {...nonNegativeInputProps}
            className={`${controlH} ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
          />
        );

      case 'dob':
        return (
          <DobField
            id={field.id}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={error}
            disabled={field.disabled}
            placeholder={field.placeholder}
            allowUnknown={field.allowUnknown}
          />
        );

      case 'name-with-initials':
        return (
          <FullNameField
            id={field.id}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={error}
            disabled={field.disabled}
            placeholder={field.placeholder}
          />
        );

      case 'date': {
        // Pass the raw stored value (Date parsing + the "Unknown" sentinel are
        // handled inside DatePicker).
        return (
          <DatePicker
            id={field.id}
            value={value}
            onChange={(date, unknown) =>
              onChange(unknown ? UNKNOWN_DATE_VALUE : date ? format(date, 'yyyy-MM-dd') : '')
            }
            disabled={field.disabled}
            className={controlH}
            allowUnknown={field.allowUnknown}
          />
        );
      }

      // 'payor' renders as a select; its options are populated with the live
      // payor list by usePayorEnhancedGroups (payor-enhanced workflows).
      case 'payor':
      case 'select':
        return (
          <SearchableSelect
            id={field.id}
            value={value ?? ''} // Use nullish coalescing to preserve false
            onValueChange={onChange}
            options={field.options || []}
            placeholder={field.placeholder}
            disabled={field.disabled}
            error={!!error}
            className={controlH}
          />
        );

      case 'multi_select': {
        const selected = Array.isArray(value)
          ? value
          : typeof value === 'string' && value
            ? [value]
            : [];
        const definedOptions =
          field.options?.map(opt => ({
            label: opt.label,
            value: String(opt.value),
          })) || [];
        // Legacy fallback: if a selected value doesn't match any defined option
        // (e.g. old free-text value before the field was changed to multi_select),
        // inject it as an ad-hoc option so it still appears checked in the dropdown.
        const definedValues = new Set(definedOptions.map(o => o.value));
        const orphanOptions = selected
          .filter(v => !definedValues.has(v))
          .map(v => ({ label: v, value: v }));
        const effectiveOptions = [...definedOptions, ...orphanOptions];
        return (
          <MultiSelect
            id={field.id}
            options={effectiveOptions}
            selected={selected}
            onChange={onChange}
            placeholder={field.placeholder || 'Select options...'}
            disabled={field.disabled}
            className={error ? 'border-destructive' : ''}
            searchable
          />
        );
      }

      case 'phone':
        return (
          <ReactPhoneInput
            inputProps={{
              id: field.id,
              className: error ? 'error' : '',
            }}
            country="us"
            value={value ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            disabled={field.disabled}
            inputClass="w-full!"
            containerClass="phone-input-container"
            inputStyle={{
              width: '100%',
              height: compact ? '32px' : '44px',
              fontSize: '14px',
              paddingLeft: '48px',
              borderRadius: '6px',
              border: error ? '1px solid #ef4444' : '1px solid hsl(var(--border))',
              backgroundColor: 'transparent',
              outline: 'none',
              transition: 'all 0.2s',
            }}
            buttonStyle={{
              borderRadius: '6px 0 0 6px',
              border: error ? '1px solid #ef4444' : '1px solid hsl(var(--border))',
              borderRight: 'none',
              backgroundColor: 'transparent',
              transition: 'all 0.2s',
            }}
            placeholder={field.placeholder}
            enableSearch
            searchPlaceholder="Search countries..."
            dropdownStyle={{
              zIndex: 99999,
              position: 'absolute',
            }}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={field.id}
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={field.placeholder}
            disabled={field.disabled}
            className={`w-full min-h-[100px] p-3 rounded-md border text-sm focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-0 ${
              error ? 'border-destructive focus:ring-destructive' : ''
            }`}
          />
        );

      case 'file':
        return (
          <FileField
            id={field.id}
            field={field}
            value={value}
            onChange={onChange}
            disabled={field.disabled}
            error={error}
            caseRecordId={caseRecordId}
          />
        );

      default:
        return null;
    }
  };

  // Compute auto-calculated value if applicable
  const autoCalculatedResult = useMemo(() => {
    if (!field.autoCalculates) return null;
    return computeAutoCalculatedValue(field, value);
  }, [field, value]);

  // Render the input field
  const inputElement = renderField();

  // `data-field-id` anchors the field for programmatic focus/scroll after a failed submit.
  // The `id` alone isn't enough: date, multi-select and file fields put no id on a focusable
  // control, so the wrapper is the only thing a caller can reliably find them by.
  return (
    <div className="space-y-2" data-field-id={field.id}>
      {!hideLabel && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Label htmlFor={field.id}>
              {field.label}
              {field.validation?.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            {/* Provenance/gap badge (R4: never a blank without a reason) */}
            {sp?.badge && (
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${
                  sp.badge.kind === 'pms'
                    ? 'border border-ai-suggestion-border bg-ai-suggestion text-ai-suggestion-foreground'
                    : sp.badge.kind === 'edited'
                      ? 'border border-ai-hint-border bg-ai-hint text-ai-hint-foreground'
                      : 'border bg-muted text-muted-foreground'
                }`}
              >
                {sp.badge.label}
              </span>
            )}
            {sp?.badge?.kind === 'edited' && sp.onRestore && (
              <button
                type="button"
                onClick={sp.onRestore}
                className="shrink-0 text-xs text-primary underline-offset-2 hover:underline"
              >
                Restore PMS value
              </button>
            )}
          </div>
          {autoCalculatedResult && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md">
              <span className="font-medium">{autoCalculatedResult.label}:</span>
              <span>{autoCalculatedResult.value}</span>
            </div>
          )}
        </div>
      )}

      {/* Wrap with InputWithSuggestion if suggestion or hint exists */}
      {hasSuggestionOrHint && sp ? (
        <InputWithSuggestion
          suggestion={sp.suggestion}
          hint={sp.hint}
          field={field}
          value={value}
          isCollapsed={sp.isCollapsed}
          isTextarea={field.type === 'textarea'}
          error={error}
          onAccept={() => {
            onChange(sp.suggestion);
            sp.onAccept();
          }}
          onReject={sp.onReject}
          onExpand={sp.onExpand}
          options={sp.options}
          onAcceptOption={optionValue => {
            onChange(optionValue);
            sp.onAcceptOption?.(optionValue);
          }}
        >
          {inputElement}
        </InputWithSuggestion>
      ) : (
        inputElement
      )}

      {field.description && <p className="text-sm text-muted-foreground">{field.description}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

export { FormFieldRenderer };
